import { useQuery } from '@tanstack/react-query';

import { clientApi } from '../../configuration';
import { usePagination } from '../../utils';

type GetPaginatedAnalyticsParams = {
  id: string;
  code: 'SPARK_STUDENTS';
  itemsByPage?: number;
  defaultPage?: number;
  orderBy?: string;
  asCsv?: boolean;
  filters?: {
    displayableName?: string;
  };
  groupBy?: string;
};

export default function usePaginatedOrganizationAnalytics({
  id: organizationId,
  code,
  filters,
  groupBy,
  orderBy,
  asCsv = false,
  itemsByPage = 10,
  defaultPage = 1,
}: GetPaginatedAnalyticsParams) {
  const [pagination, currentRange, getTotalPages] = usePagination({ itemsByPage, defaultPage });
  const paginationRange = `${currentRange.start}-${currentRange.end}`;

  const paginatedQueryResponse = useQuery(
    [
      'paginatedOrganizationAnalytics',
      organizationId,
      [
        {
          code,
          filters,
          orderBy,
          groupBy,
          pagination: paginationRange,
        },
      ],
      asCsv,
    ],
    async () => {
      const ocClient = clientApi.getOCClient();
      const { employer } = ocClient.helpers;

      const { body } = await employer.organizations.id(organizationId).analytics.find({
        reports: [
          {
            code,
            filters,
            orderBy,
            groupBy,
            pagination: paginationRange,
          },
        ],
        asCsv,
      });
      const [{ data: items, pagination: responseHeaderPaginationLike }] = body;

      let totalItems = items.length;

      if (responseHeaderPaginationLike) {
        // responseHeaderPaginationLike is a string with this format: "0-5/8"
        const headerData = responseHeaderPaginationLike.split('/');
        totalItems = +headerData[1];
      }

      return {
        items,
        totalItems,
        totalPages: getTotalPages(totalItems),
      };
    },
    { retry: 1, refetchOnWindowFocus: false, keepPreviousData: true }
  );

  return {
    ...paginatedQueryResponse,
    pagination,
  };
}
